/**
 * Dropdown chọn loại hình BĐS — variant inline (panel) hoặc sheet (chip row).
 *
 * Hiển thị icon danh mục và số tin đăng nếu có.
 */
import { useEffect, useRef } from 'react';
import { getCategoryIcon } from '../../shared/utils/categoryIcon';
import { getCategoryName } from '../../shared/utils/categoryLabel';
import { AnimatedPanel } from './AnimatedPanel';

const CheckIcon = () => (
  <svg className="category-dropdown-check" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="category-dropdown-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Dropdown chọn danh mục BĐS.
 * @param {Object} props
 * @param {Array} [props.categories]
 * @param {string} [props.value] - ID danh mục đang chọn
 * @param {Function} props.onChange
 * @param {string} props.label
 * @param {string} props.allLabel
 * @param {string} props.placeholder
 * @param {string} [props.closeLabel]
 * @param {'inline'|'sheet'} [props.variant]
 * @param {boolean} props.isOpen
 * @param {Function} [props.onOpenChange]
 */
export const CategoryDropdown = ({
  categories = [],
  value = '',
  onChange,
  label,
  allLabel,
  placeholder,
  closeLabel = 'Đóng',
  variant = 'inline',
  isOpen,
  onOpenChange,
}) => {
  const rootRef = useRef(null);
  const selectedCategory = categories.find((category) => String(category.id) === String(value));
  const displayValue = selectedCategory ? getCategoryName(selectedCategory) : placeholder;

  const handleSelect = (categoryId) => {
    onChange(categoryId);
    onOpenChange?.(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDownOutside);

    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside);
    };
  }, [isOpen, onOpenChange]);

  const options = (
    <div className="category-dropdown-list" role="listbox" aria-label={label}>
      <button
        type="button"
        role="option"
        aria-selected={!value}
        className={`category-dropdown-option ${!value ? 'is-active' : ''}`}
        onClick={() => handleSelect('')}
      >
        <span className="category-dropdown-icon" aria-hidden="true">
          🏡
        </span>
        <span className="category-dropdown-copy">
          <strong>{allLabel}</strong>
        </span>
        {!value && <CheckIcon />}
      </button>

      {categories.map((category) => {
        const isActive = String(category.id) === String(value);

        return (
          <button
            key={category.id}
            type="button"
            role="option"
            aria-selected={isActive}
            className={`category-dropdown-option ${isActive ? 'is-active' : ''}`}
            onClick={() => handleSelect(String(category.id))}
          >
            <span className="category-dropdown-icon" aria-hidden="true">
              {getCategoryIcon(category.icon)}
            </span>
            <span className="category-dropdown-copy">
              <strong>{getCategoryName(category)}</strong>
              {category.properties_count > 0 && (
                <span className="category-dropdown-meta">{category.properties_count} tin</span>
              )}
            </span>
            {isActive && <CheckIcon />}
          </button>
        );
      })}
    </div>
  );

  if (variant === 'sheet') {
    return (
      <div className="category-dropdown category-dropdown--sheet">
        <div className="dropdown-chip-row category-dropdown-chips" role="listbox" aria-label={label}>
          <button
            type="button"
            role="option"
            aria-selected={!value}
            className={`dropdown-chip ${!value ? 'is-active' : ''}`}
            onClick={() => onChange('')}
          >
            {allLabel}
          </button>

          {categories.map((category) => {
            const isActive = String(category.id) === String(value);

            return (
              <button
                key={category.id}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`dropdown-chip ${isActive ? 'is-active' : ''}`}
                onClick={() => onChange(String(category.id))}
              >
                {getCategoryName(category)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`category-dropdown category-dropdown--inline ${isOpen ? 'is-open' : ''}`.trim()}
    >
      <button
        type="button"
        className={`category-dropdown-trigger ${isOpen ? 'is-open' : ''} ${value ? 'has-value' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
        onClick={() => onOpenChange?.(!isOpen)}
      >
        <span className="category-dropdown-trigger-value">{displayValue}</span>
        <ChevronIcon />
      </button>

      <AnimatedPanel
        isOpen={isOpen}
        className="dropdown-shell category-dropdown-panel"
        role="dialog"
        aria-label={label}
      >
        <div className="dropdown-shell-header category-dropdown-panel-header">
          <strong>{label}</strong>
          <button type="button" className="dropdown-shell-close" onClick={() => onOpenChange?.(false)}>
            {closeLabel}
          </button>
        </div>
        <div className="dropdown-shell-body category-dropdown-panel-body">{options}</div>
      </AnimatedPanel>
    </div>
  );
};
